/**
 * Fábrica da fonte de dados (§14.8.1, §14.9.7; FE §3.9, DE-B09, DE-B13) e carregador do DevPanel. Aqui ficam os ÚNICOS
 * `import()` de `src/sim/`, todos atrás de `__SIM_ENABLED__` (§2.2.2 regra 2): no build de produção a constante é
 * `false` em tempo de compilação, o ramo inteiro some e a fonte é sempre o `HttpSource` (a query é ignorada,
 * AC-2.5-02, AC-2.9-04). `FixtureSource` e `ReplaySource` também só entram por `import()` dentro do ramo.
 *
 * Fora da produção (dev e prévia): `?fonte=` tem precedência sobre `config.mode` (`'simulation'` → simulação); valor
 * inválido é ignorado. Proteção de data (M20): fora de host local ou de prévia, a partir de 23/10 (Brasília) nenhuma
 * fonte de teste inicia e vale o `HttpSource`. Nomes fictícios (§14.9.8, DR-17): fora de host local (ou com
 * `nomes=ficticios`), também as fontes `http`, `replay` e `fixture` servem o cadastro (e o 1º turno) com nomes fictícios
 * e sem fotos; a simulação já faz isso sozinha. No build do alvo `ensaio` (sem simulação), o `HttpSource` também (DR-17).
 */
import type { ComponentType } from 'preact';
import type { AppConfig } from '../config';
import type { ValidationConfig } from '../contracts/validate';
import { HttpSource } from '../data/HttpSource';
import { loadStatics } from '../data/loadStatic';
import type { DataSource, Statics } from '../data/source';

/** Valores de `?fonte=` (§14.9.7). */
export type SourceKind = 'sim' | 'replay' | 'fixture' | 'http';

/** Ambiente da escolha: host (nomes e proteção de data), relógio do aparelho (data do congelamento) e build. */
export interface SourceEnv {
  /** `location.host`. */
  host: string;
  /** Relógio do aparelho (ms): só para a proteção de data de 23/10 (código, não dado). */
  nowMs: number;
  /** Build de produção (`VITE_ENV_NAME = production`): I-17 e recusa de estático de amostra (AC-2.7-08). */
  production: boolean;
}

/**
 * Build de produção para a validação (I-17: só `origin = 'tse'`) e para os estáticos (amostra recusada). Vale o alvo
 * `prod` (`envName = production`, §2.9.1), não a falta de simulação: o `ensaio` e o `build:ci` também não têm
 * `src/sim/`, mas servem feed de simulação (`ensaio`, faixa ligada) e de fixtures (CI), que precisam ser aceitos.
 */
export const PRODUCTION_BUILD: boolean = !__SIM_ENABLED__ && import.meta.env.VITE_ENV_NAME === 'production';

export function defaultSourceEnv(): SourceEnv {
  return {
    host: typeof location === 'undefined' ? '' : location.host,
    nowMs: Date.now(),
    production: PRODUCTION_BUILD,
  };
}

/** Parte da configuração que a validação dos estáticos usa. */
function validationConfig(config: AppConfig): ValidationConfig {
  return { round: config.round, election_date: config.election_date, mode: config.mode };
}

/** Fonte única do build de produção (§14.8.2): `config.feed_origins`, com failover. */
function httpSource(config: AppConfig, env: SourceEnv, statics?: () => Promise<Statics>): DataSource {
  return new HttpSource({
    origins: config.feed_origins,
    config: validationConfig(config),
    rejectSample: env.production,
    statics,
  });
}

/** `?fonte=` válido, ou null (vale `config.mode`). */
export function requestedKind(query: URLSearchParams): SourceKind | null {
  const v = query.get('fonte');
  return v === 'sim' || v === 'replay' || v === 'fixture' || v === 'http' ? v : null;
}

/** Rótulo de fixture `{cenário}@{marco}` (§14.12.1). */
const FIXTURE_LABEL_RE = /^[a-z][a-z0-9_]*@[a-z][a-z0-9_]*$/;
/** Pasta gravada do replay: caminho relativo à origem, sem `..`, sem esquema nem `//`. */
const ARCHIVE_PATH_RE = /^(?!.*\.\.)[A-Za-z0-9_-][A-Za-z0-9_./-]*$/;

/** Pasta do replay (`?arquivo=`) como URL da mesma origem, terminando em "/"; null se inválida. */
export function archiveBase(value: string | null): string | null {
  if (value === null) return null;
  const raw = value.trim();
  if (raw.includes('//')) return null;
  const v = raw.replace(/^\//, '');
  if (v === '' || !ARCHIVE_PATH_RE.test(v)) return null;
  return `/${v.endsWith('/') ? v : `${v}/`}`;
}

/**
 * Escolhe e cria a fonte (§14.8.1). Assíncrona porque o código de teste só chega por `import()`. Nunca lança por
 * parâmetro inválido: o parâmetro é ignorado e vale o padrão (§14.9.7).
 */
export async function createSource(
  config: AppConfig,
  query: URLSearchParams,
  env: SourceEnv = defaultSourceEnv(),
): Promise<DataSource> {
  // Constante de build: no build sem simulação o ramo (e os import() dele) some.
  if (__SIM_ENABLED__) return createTestSource(config, query, env);
  // Alvo `ensaio` (DR-17; ADENDO, AC-2.9-06): URL pública fora da produção, sem simulação no build, lendo o feed de
  // ensaio: cadastro e 1º turno com nomes fictícios e retratos em iniciais. Constante de build: some na produção.
  if (import.meta.env.VITE_ENV_NAME === 'ensaio') return fictitiousHttpSource(config, env);
  return httpSource(config, env);
}

/** Semente fixa dos nomes do ensaio (a padrão da simulação, §14.9.3): os mesmos nomes em toda visita. */
export const ENSAIO_NAMES_SEED = 2026;

/**
 * `HttpSource` com nomes fictícios (§14.9.8, DR-17): o cadastro e o 1º turno passam por `fictitiousStatics` (chunk
 * próprio, só no build do ensaio e nos de teste). Exportada para o teste de unidade; na produção não é chamada.
 */
export async function fictitiousHttpSource(
  config: AppConfig,
  env: SourceEnv,
  seed: number = ENSAIO_NAMES_SEED,
): Promise<DataSource> {
  const { fictitiousStatics } = await import('../sim/fictitiousNames');
  const vcfg = validationConfig(config);
  let memo: Promise<Statics> | null = null;
  return httpSource(config, env, () => {
    memo ??= loadStatics({ config: vcfg, rejectSample: env.production }).then((s) => fictitiousStatics(s, seed));
    return memo;
  });
}

/** Fontes de teste (dev e prévia): só existe no build com `__SIM_ENABLED__`. */
async function createTestSource(config: AppConfig, query: URLSearchParams, env: SourceEnv): Promise<DataSource> {
  const sim = await import('../sim/SimSource');
  const { parseSimQuery } = await import('../sim/scenarios');
  const q = parseSimQuery(query, { host: env.host });
  let kind: SourceKind = q.source ?? (config.mode === 'simulation' ? 'sim' : 'http');
  // M20: depois do congelamento, fora de host local ou de prévia, nada de dado de teste.
  if (kind !== 'http' && !sim.simAllowed(env.host, env.nowMs)) kind = 'http';
  const vcfg = validationConfig(config);
  const simSource = () =>
    new sim.SimSource(q.params, { config: vcfg, host: env.host, commentary: config.features.commentary });
  if (kind === 'sim') return simSource();

  // Nomes fictícios fora de host local ou com nomes=ficticios (§14.9.8; parseSimQuery já aplica a regra do host).
  const baseStatics = () => loadStatics({ config: vcfg, rejectSample: env.production });
  let shownStatics: (() => Promise<Statics>) | undefined;
  if (q.params.names === 'fictitious') {
    const { fictitiousStatics } = await import('../sim/fictitiousNames');
    let memo: Promise<Statics> | null = null;
    shownStatics = () => {
      memo ??= baseStatics().then((s) => fictitiousStatics(s, q.params.seed));
      return memo;
    };
  }
  const fallback = () => (config.mode === 'simulation' ? simSource() : httpSource(config, env, shownStatics));

  if (kind === 'fixture') {
    const label = q.fixture !== null && FIXTURE_LABEL_RE.test(q.fixture) ? q.fixture : null;
    if (label === null) {
      console.warn('[fonte] fixture sem rótulo válido ({cenário}@{marco}); vale o padrão');
      return fallback();
    }
    const { FixtureSource } = await import('../data/FixtureSource');
    return new FixtureSource({ name: label, statics: shownStatics ?? baseStatics });
  }
  if (kind === 'replay') {
    const baseUrl = archiveBase(q.archive);
    if (baseUrl === null) {
      console.warn('[fonte] replay sem pasta válida (arquivo=); vale o padrão');
      return fallback();
    }
    const { ReplaySource } = await import('../data/ReplaySource');
    return new ReplaySource({
      baseUrl,
      config: vcfg,
      speed: q.params.speed,
      startTMin: q.params.startTMin,
      paused: q.params.paused,
      statics: shownStatics,
    });
  }
  return httpSource(config, env, shownStatics);
}

/**
 * `painel` (§14.9.7): `1` mostra o DevPanel, `0` esconde; padrão: ligado no `npm run dev`, desligado na prévia. Sem
 * `__SIM_ENABLED__`, nunca.
 */
export function devPanelWanted(query: URLSearchParams): boolean {
  if (__SIM_ENABLED__) {
    const v = query.get('painel');
    if (v === '1' || v === '0') return v === '1';
    return import.meta.env.DEV === true;
  }
  return false;
}

/** O componente do painel (§14.9.9), com as props de src/sim/DevPanel.tsx. */
export type DevPanelComponent = typeof import('../sim/DevPanel')['DevPanel'];
/** Props do painel (fonte, recriação e leituras), sem importar src/sim/ em valor. */
export type DevPanelHostProps = Parameters<DevPanelComponent>[0];

/** Carrega o DevPanel no chunk próprio (DE-B13); null no build sem simulação. */
export async function loadDevPanel(): Promise<ComponentType<DevPanelHostProps> | null> {
  if (__SIM_ENABLED__) {
    const m = await import('../sim/DevPanel');
    return m.DevPanel;
  }
  return null;
}
